import React from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { sensorFullNameKey } from 'commons/src/constants';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { sensorRating, formatRating, sensorShowsRatingLabels } from 'commons/src/sensorRatingUtils';
import { PDFContext, ReportSensorThresholds } from '../../../../../models/common';
import { formatSensorValue, formatSensorUnit, getSensorIconSource, getStatusPillColors } from '../utils/pdfHelpers';
import iaqInsight2PdfStyles from '../utils/pdfStyles';

type IaqInsight2SummaryCardsProps = {
    translationFunction: PDFContext;
    reports: { [sensor: string]: { averageValueWithinOpeningHours: number; averageValue: number } };
    thresholds: { [sensor: string]: ReportSensorThresholds };
};

const IaqInsight2SummaryCards = ({
    translationFunction,
    reports,
    thresholds,
}: IaqInsight2SummaryCardsProps): React.ReactElement => {
    // Define custom sensor order for IAQInsight2: CO2, Temp, Humidity, VOC, PM2.5, PM1, Light, Noise
    const sensorOrder = [
        SensorTypes.co2,
        SensorTypes.temp,
        SensorTypes.humidity,
        SensorTypes.voc,
        SensorTypes.pm25,
        SensorTypes.pm1,
        SensorTypes.light,
        SensorTypes.noise,
    ];
    const sensorOrderMap = new Map(sensorOrder.map((sensor, index) => [sensor, index]));
    const sortedSensorTypeKeys = Object.keys(reports).sort((sensorA, sensorB) => {
        const indexA = sensorOrderMap.get(sensorA as SensorTypes) ?? 999;
        const indexB = sensorOrderMap.get(sensorB as SensorTypes) ?? 999;
        return indexA - indexB;
    });

    const itemsPerRow = 4;
    const rows: string[][] = [];
    for (let i = 0; i < sortedSensorTypeKeys.length; i += itemsPerRow) {
        rows.push(sortedSensorTypeKeys.slice(i, i + itemsPerRow));
    }

    return (
        <View style={iaqInsight2PdfStyles.topSummaryCardsGrid} wrap={false}>
            {rows.map(row => (
                <View key={row.join('-')} style={iaqInsight2PdfStyles.summaryCardsRow}>
                    {row.map(sensorTypeKey => {
                        const sensorReportData = reports[sensorTypeKey];
                        const sensorThresholdConfiguration = thresholds[sensorTypeKey];
                        if (!sensorReportData || !sensorThresholdConfiguration) return null;

                        const { averageValueWithinOpeningHours } = sensorReportData;

                        const hasValidData =
                            averageValueWithinOpeningHours !== undefined &&
                            averageValueWithinOpeningHours !== null &&
                            !Number.isNaN(averageValueWithinOpeningHours);

                        const sensorValueRating = hasValidData
                            ? sensorRating(sensorThresholdConfiguration.ranges, averageValueWithinOpeningHours)
                            : undefined;
                        const formattedSensorValue = hasValidData
                            ? formatSensorValue(averageValueWithinOpeningHours, sensorThresholdConfiguration.unit)
                            : 'N/A';
                        const formattedMeasurementUnit = formatSensorUnit(
                            sensorTypeKey,
                            sensorThresholdConfiguration.unit
                        );
                        const formattedSensorDisplayName = translationFunction(
                            sensorFullNameKey(sensorTypeKey)
                        ).replace('CO₂', 'CO2');
                        const sensorIconImageSource = getSensorIconSource(sensorTypeKey);
                        const statusPillColors = getStatusPillColors(sensorValueRating);
                        const shouldShowStatusPill = sensorValueRating && sensorShowsRatingLabels(sensorTypeKey);

                        return (
                            <View key={`summary-${sensorTypeKey}`} style={iaqInsight2PdfStyles.sensorSummaryItem}>
                                {sensorIconImageSource && (
                                    <Image src={sensorIconImageSource} style={iaqInsight2PdfStyles.metricCardIcon} />
                                )}
                                <View style={iaqInsight2PdfStyles.sensorSummaryContent}>
                                    <View style={iaqInsight2PdfStyles.sensorSummaryHeader}>
                                        <Text style={iaqInsight2PdfStyles.sensorSummaryName}>
                                            {formattedSensorDisplayName}
                                        </Text>
                                        {shouldShowStatusPill && (
                                            <View
                                                style={[
                                                    iaqInsight2PdfStyles.statusPill,
                                                    {
                                                        backgroundColor: statusPillColors.backgroundColor,
                                                    },
                                                ]}
                                            >
                                                <Text
                                                    style={[
                                                        iaqInsight2PdfStyles.statusPillText,
                                                        {
                                                            color: statusPillColors.textColor,
                                                        },
                                                    ]}
                                                >
                                                    {formatRating(sensorValueRating)}
                                                </Text>
                                            </View>
                                        )}
                                    </View>
                                    <View style={iaqInsight2PdfStyles.sensorSummaryValue}>
                                        <Text style={iaqInsight2PdfStyles.sensorSummaryValueText}>
                                            {formattedSensorValue}
                                        </Text>
                                        {hasValidData && (
                                            <Text style={iaqInsight2PdfStyles.sensorSummaryUnit}>
                                                {formattedMeasurementUnit}
                                            </Text>
                                        )}
                                    </View>
                                </View>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

export default IaqInsight2SummaryCards;
